/**
 * A service and the parts it carries, drawn as ONE thing.
 *
 * The operator sees the breakdown — every part, its price, and what the bundle
 * is worth. The customer sees a single line. This wrapper is what makes that
 * difference visible on the operator's side, so nobody has to infer from a flat
 * list that three rows are really one sale.
 *
 * Presentational on purpose: callers keep rendering their own line rows and
 * pass them as children. That is what lets estimates, jobs, invoices and
 * proposals share the bundle framing without giving up their row internals.
 */
import { Fragment, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Clock,
  Info,
  Layers,
  Receipt,
} from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { TableCell, TableRow } from '@/components/atoms/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/atoms/tooltip';
import { cn } from '@/lib/utils';
import { unfoldsOntoItsOwnLine } from '@/lib/sales/service-components';
import { rendersOnCustomerDocument, type LineEconomics } from '@/lib/sales/line-economics';
import {
  analyzeBundles,
  componentBillsOwnLine,
  DISPLAY_MODE_TAX_HINT,
  type BundleEconomics,
  type BundleTaxWarning,
} from '@/lib/sales/service-bundles';
import type { ComponentGroupableLine } from '@/lib/sales/service-components';
import { billsAtLineRate, dynamicLaborBasis } from '@/lib/sales/dynamic-service-price';
import { flatServiceLaborLines, laborLinesHours, laborRateLabel } from '@/lib/sales/service-labor';
import type { ServiceLaborLine } from '@/types/pricebook';
import type { TaxLineType } from '@/types/tax';

/** What `renderGroupedLines` needs off a line beyond the grouping keys. */
export interface BundleGroupableLine extends ComponentGroupableLine {
  name?: string;
  quantity?: number;
  isOptional?: boolean | null;
  taxLineType?: string | null;
  /**
   * The service's own time, in whichever shape the document carries it: a
   * dynamic service configures labor LINES, a flat one may carry rows or the
   * older `estimatedHours`. All three are read through the same helper, so a
   * service does not lose its hours by being priced differently.
   */
  estimatedHours?: number | null;
  laborCostLines?: ServiceLaborLine[] | null;
  laborCostRate?: number | null;
  dynamicPricingConfig?: { laborLines?: ServiceLaborLine[] | null } | null;
}

/**
 * The hours behind a service LINE, whatever priced it.
 *
 * Every source of the time — the dynamic configuration, the labor rows, the
 * stored `estimatedHours` — states it PER UNIT, so the line's hours are that
 * time multiplied by how many of the service are being sold. The card would
 * otherwise head a doubled price with a single unit's time, which is the half-
 * responding quantity field NOT-630 reported.
 */
export function serviceLineHours(line: BundleGroupableLine): number {
  const lines = serviceLaborLines(line);
  const units = Number.isFinite(line.quantity) ? Math.max(0, line.quantity as number) : 1;
  return laborLinesHours(lines) * units;
}

function serviceLaborLines(line: BundleGroupableLine): ServiceLaborLine[] {
  const dynamicLines = line.dynamicPricingConfig?.laborLines;
  return dynamicLines && dynamicLines.length > 0
    ? dynamicLines
    : flatServiceLaborLines({
        laborCostLines: line.laborCostLines,
        estimatedHours: line.estimatedHours,
        laborCostRate: line.laborCostRate,
      });
}

export interface ServiceLaborDetail {
  label: string;
  hours: number;
  amount: number;
  rateName: string;
  rates: number[];
}

/**
 * The individual labor tasks behind a service line.
 *
 * A materialized dynamic service freezes profile tiers on each row, while a
 * fixed row bills from its live fixed rate. `dynamicLaborBasis` is the pricing
 * source of truth for both, so the detail shown in Billing cannot disagree
 * with the labor amount already included in the service price. Values are per
 * service in storage and are multiplied by the document quantity here.
 */
export function serviceLineLaborDetails(line: BundleGroupableLine): ServiceLaborDetail[] {
  const units = Number.isFinite(line.quantity) ? Math.max(0, line.quantity as number) : 1;

  return serviceLaborLines(line).map((labor, index) => {
    const basis = dynamicLaborBasis({ enabled: true, laborLines: [labor] });
    const fixedRate = Number.isFinite(labor.fixedRate) ? Math.max(0, labor.fixedRate as number) : 0;
    const tiers =
      billsAtLineRate(labor) || !labor.resolvedTiers?.length
        ? [{ rate: fixedRate }]
        : labor.resolvedTiers;
    const rates = [...new Set(tiers.map((tier) => tier.rate).filter(Number.isFinite))].sort(
      (a, b) => a - b,
    );

    return {
      label: labor.label?.trim() || `Labor ${index + 1}`,
      hours: (basis?.hours ?? Math.max(0, labor.durationMinutes || 0) / 60) * units,
      amount: (basis?.amount ?? 0) * units,
      rateName: laborRateLabel(labor, []),
      rates,
    };
  });
}

/** "7h", "1h 30m", "45m" — hours as a person says them. */
export function formatLaborHours(hours: number): string {
  const whole = Math.floor(hours);
  const minutes = Math.round((hours - whole) * 60);
  if (minutes === 60) return `${whole + 1}h`;
  if (whole === 0) return `${minutes}m`;
  return minutes === 0 ? `${whole}h` : `${whole}h ${minutes}m`;
}

export interface ServiceBundleGroupProps {
  /** The service's name, for the customer-preview line. */
  serviceName: string;
  economics: BundleEconomics;
  warning: BundleTaxWarning | null;
  isRolledUp: boolean;
  treatment: TaxLineType | null | undefined;
  partCount: number;
  /**
   * Of the parts in the sale, how many bill the customer on a line of their
   * own, and how many are inside the service's price. Decided by each part's
   * TREATMENT, never by its amount — a billed part priced at $0.00 is still a
   * billed part, and must not read as "included in the service price".
   */
  billedPartCount?: number;
  includedPartCount?: number;
  /**
   * How many parts draw a ROW on the customer's document — see `partCounts`.
   * Not the same as how many charge: a visible included EQUIPMENT prints as
   * "Included", a non-chargeable MATERIAL prints nothing at all.
   */
  customerRowCount?: number;
  /**
   * Does the SERVICE itself draw one? A hidden service prints no row of its
   * own — `customerVisibleRows` drops it — so the count of what the customer
   * reads must not assume it is there.
   */
  serviceDrawsRow?: boolean;
  formatCurrency: (value: number) => string;
  /**
   * The service's own hours. Stated beside the parts because the two together
   * are what the service IS — the parts were listed and the labour that
   * installs them was not, so a $2,066.82 service read as three materials and
   * an unexplained remainder.
   */
  laborHours?: number;
  /** The named labor tasks that make up `laborHours`. */
  laborDetails?: ServiceLaborDetail[];
  /** The parent service's own row. */
  children: ReactNode;
  /** The component rows, nested under the service. */
  parts: ReactNode;
  /** Omit to render read-only (customer-facing previews, locked documents). */
  onDisplayModeChange?: (mode: 'ITEMIZED' | 'ROLLED_UP') => void;
  /**
   * The viewer may not see pricing. The bundle FRAMING is not pricing — which
   * rows are one sale is operational — so it keeps rendering; every currency
   * figure in it does not.
   */
  hideAmounts?: boolean;
  className?: string;
}

export function ServiceBundleGroup({
  serviceName,
  economics,
  warning,
  isRolledUp,
  treatment,
  partCount,
  billedPartCount = 0,
  includedPartCount = partCount,
  customerRowCount = billedPartCount,
  serviceDrawsRow = true,
  formatCurrency,
  laborHours = 0,
  laborDetails = [],
  children,
  parts,
  onDisplayModeChange,
  hideAmounts = false,
  className,
}: ServiceBundleGroupProps) {
  const [partsOpen, setPartsOpen] = useState(true);
  const [laborOpen, setLaborOpen] = useState(true);
  const readOnly = !onDisplayModeChange;

  return (
    // Its own provider: this drops into four different line surfaces, and
    // requiring each to remember a TooltipProvider is a crash waiting for
    // whichever one forgets. Radix providers nest harmlessly.
    <TooltipProvider>
      <div
        className={cn(
          'rounded-lg border border-primary/20 bg-primary/[0.015]',
          warning?.level === 'high' && 'border-destructive/40',
          className,
        )}
      >
        <BundleSummary
          economics={economics}
          isRolledUp={isRolledUp}
          partCount={partCount}
          customerRowCount={customerRowCount}
          serviceDrawsRow={serviceDrawsRow}
          billedPartCount={billedPartCount}
          includedPartCount={includedPartCount}
          formatCurrency={formatCurrency}
          hideAmounts={hideAmounts}
          className="border-b border-primary/15 px-4 py-2.5"
        />

        <div className="px-1 py-1">{children}</div>

        {/* The service's own time, stated the way the parts are. Without it the
            group listed what the job consumes and not what it takes to do — the
            labour is most of the price on a service like this, and it was the
            one figure nothing on the row accounted for. */}
        {laborHours > 0 ? (
          <div className="px-1 pb-1">
            <button
              type="button"
              onClick={() => setLaborOpen((open) => !open)}
              className="flex w-full items-center gap-1.5 px-2 py-1 text-left text-xs text-muted-foreground hover:text-foreground"
              aria-expanded={laborOpen}
            >
              {laborOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              <Clock size={13} className="shrink-0" />
              Labor
              <span className="text-muted-foreground/60">· {formatLaborHours(laborHours)}</span>
              {!hideAmounts && laborDetails.length > 0 ? (
                <span className="ml-auto tabular-nums text-muted-foreground/70">
                  {formatCurrency(laborDetails.reduce((total, detail) => total + detail.amount, 0))}
                </span>
              ) : null}
            </button>
            {laborOpen && laborDetails.length > 0 ? (
              <div className="ml-3 divide-y border-l-2 border-primary/20 pl-2">
                {laborDetails.map((detail, index) => (
                  <div
                    key={`${detail.label}-${index}`}
                    className="flex flex-wrap items-center gap-x-3 gap-y-0.5 px-3 py-2 text-xs"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-foreground">{detail.label}</p>
                      {!hideAmounts ? (
                        <p className="text-muted-foreground">
                          {detail.rateName}
                          {detail.rates.length > 0
                            ? ` · ${formatLaborRateRange(detail.rates, formatCurrency)}`
                            : ''}
                        </p>
                      ) : null}
                    </div>
                    <span className="tabular-nums text-muted-foreground">
                      {formatLaborHours(detail.hours)}
                    </span>
                    {!hideAmounts ? (
                      <span className="min-w-20 text-right font-medium tabular-nums text-foreground">
                        {formatCurrency(detail.amount)}
                      </span>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {partCount > 0 ? (
          <div className="px-1 pb-1">
            <button
              type="button"
              onClick={() => setPartsOpen((open) => !open)}
              className="flex w-full items-center gap-1.5 px-2 py-1 text-left text-xs text-muted-foreground hover:text-foreground"
              aria-expanded={partsOpen}
            >
              {partsOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              {/* Nothing is "included" in a service that is not being sold,
                  and nothing under it is sold on its own either: a declined
                  service takes every part with it (#2452). The drawer still
                  lists them — they are on the document — and says what they
                  are: not in the sale. */}
              {economics.isSold ? 'Included parts' : 'Parts, not in the sale'}
              {!hideAmounts && economics.partsAmount > 0 ? (
                <span className="text-muted-foreground/60">
                  · {formatCurrency(economics.partsAmount)}
                </span>
              ) : null}
            </button>
            {partsOpen ? (
              // The left rule is the whole point of the indent: it says these
              // rows belong to the service above rather than standing beside it.
              <div className="ml-3 border-l-2 border-primary/20 pl-2">{parts}</div>
            ) : null}
          </div>
        ) : null}

        <BundleTaxControls
          economics={economics}
          billedPartCount={billedPartCount}
          customerRowCount={customerRowCount}
          serviceDrawsRow={serviceDrawsRow}
          isRolledUp={isRolledUp}
          treatment={treatment}
          readOnly={readOnly}
          formatCurrency={formatCurrency}
          serviceName={serviceName}
          hideAmounts={hideAmounts}
          onDisplayModeChange={onDisplayModeChange}
        />

        {warning ? <BundleWarningBanner warning={warning} /> : null}
      </div>
    </TooltipProvider>
  );
}

function formatLaborRateRange(rates: number[], formatCurrency: (value: number) => string): string {
  const low = rates[0];
  const high = rates[rates.length - 1];
  if (low == null || high == null) return '';
  return low === high
    ? `${formatCurrency(low)}/hr`
    : `${formatCurrency(low)}–${formatCurrency(high)}/hr`;
}

/**
 * The one-line statement of what a bundle is and what it is worth.
 *
 * Shared by the card and the table so both say the same thing: the framing
 * ("this is one sale"), how many parts are in it, the arithmetic that gets to
 * the bundle price, and what was offered but is NOT in that price. A read-only
 * surface that re-invented this copy is exactly how the detail view ended up
 * showing three sibling rows with nothing tying them together.
 */
export function BundleSummary({
  economics,
  isRolledUp,
  partCount,
  billedPartCount = 0,
  includedPartCount = partCount,
  customerRowCount = 0,
  serviceDrawsRow = true,
  formatCurrency,
  hideAmounts = false,
  className,
}: {
  economics: BundleEconomics;
  /** ROLLED_UP puts one line on the customer's document; ITEMIZED prints each. */
  isRolledUp: boolean;
  partCount: number;
  /** See ServiceBundleGroupProps — by treatment, not by amount. */
  billedPartCount?: number;
  includedPartCount?: number;
  /** How many parts draw a customer-facing ROW — see `partCounts`. */
  customerRowCount?: number;
  /** Does the service itself draw one? A hidden service does not. */
  serviceDrawsRow?: boolean;
  formatCurrency: (value: number) => string;
  /** Viewer cannot see pricing: keep the framing, drop every figure. */
  hideAmounts?: boolean;
  className?: string;
}) {
  // What the customer's document actually draws — asked the same way in both
  // display modes: the service WHEN IT DRAWS A ROW (a hidden one does not),
  // plus every part that draws one of its own. ROLLED_UP is not "always one
  // line": it folds parts into the service's row, which `customerRowCount`
  // already accounts for, so a HIDDEN rolled-up service over one unfolded part
  // is one line — the part's.
  const customerLineCount = (serviceDrawsRow ? 1 : 0) + customerRowCount;

  return (
    <TooltipProvider>
      <div className={cn('flex flex-wrap items-center gap-2', className)}>
        <Layers size={13} className="shrink-0 text-primary/70" />
        {/* "Service", not "Service bundle": a BUNDLE is a different item type
            — several items sold together as one customer-facing line — and
            calling this grouping one made the two indistinguishable on screen.
            What this is, is a service and the parts it consumes. */}
        <span className="text-xs font-medium">
          {economics.isSold ? 'Service' : 'Service not included'}
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Labelled so the hint is reachable by keyboard and by name —
                an icon with no accessible name is a hint only a mouse finds. */}
            <button
              type="button"
              aria-label="What this bundle shows the customer"
              className="shrink-0 cursor-help text-muted-foreground"
            >
              <Info size={12} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            {economics.isSold && customerLineCount === 0 ? (
              <>
                {/* Sold, and nothing on the customer&rsquo;s copy prints for it:
                    a hidden service whose parts are hidden too. Saying "a
                    single line" there described a row that does not exist —
                    and the money still counts, which is the fact worth
                    stating. */}
                A service and the parts it consumes, sold as one thing. Nothing prints on the
                customer&rsquo;s copy for it — everything here is hidden — while its money still
                counts toward the total
                {hideAmounts ? '' : `, ${formatCurrency(economics.billedBundleTotal)}`}.
              </>
            ) : economics.isSold ? (
              <>
                A service and the parts it consumes, sold as one thing. You see the breakdown;
                {/* What the customer's document actually shows, which is the
                    same count the footer quotes. Not the display mode alone:
                    an ITEMIZED bundle whose parts are all hidden or folded
                    still prints ONE line, and an included MATERIAL never
                    prints at all — so `isRolledUp` promised separate lines
                    over documents that have none. The BILLED
                    figure either way: an included part's own amount is inside
                    the service's price, not a second sum the customer reads. */}
                {customerLineCount > 1
                  ? ' the customer sees these as separate lines totalling'
                  : ' the customer sees a single line'}
                {hideAmounts ? '' : ` ${formatCurrency(economics.billedBundleTotal)}`}.
              </>
            ) : (
              <>
                This service is not selected, so nothing is grouped: a part billed on its own line
                is sold on its own and taxed on its own treatment. A part included in the
                service&rsquo;s price had no line but that service&rsquo;s, so it leaves the sale
                with it.
              </>
            )}
          </TooltipContent>
        </Tooltip>

        <Badge variant="secondary" className="h-5 text-[10px] font-normal">
          {partCount === 1 ? '1 part' : `${partCount} parts`}
        </Badge>

        {/* Offered and not taken. Said here rather than left to the reader to
            work out from a row that shows a price and contributes nothing.
            Without pricing the fact still matters, so it is said without the
            figure rather than dropped. */}
        {economics.unselectedPartsAmount > 0 ? (
          <span className="text-[11px] text-muted-foreground">
            {hideAmounts
              ? 'Some parts not included'
              : `${formatCurrency(economics.unselectedPartsAmount)} not included`}
          </span>
        ) : null}

        {hideAmounts ? null : (
          <BundleArithmetic
            economics={economics}
            billedPartCount={billedPartCount}
            includedPartCount={includedPartCount}
            formatCurrency={formatCurrency}
          />
        )}
      </div>
    </TooltipProvider>
  );
}

/**
 * The figures that get to the bundle's subtotal, each one named.
 *
 * Every figure here is a SUBTOTAL — before discounts and before tax: the
 * service's price, the parts billed on top of it, and their sum, the same
 * number the totals panel's Subtotal row is built from. It used to read
 * `$710.00 + $0.00 = $710.00` with no labels, and beside a row showing $35.50
 * tax and a $745.50 total that was read as "price + tax = total" — on a flat
 * service whose one billed part happened to equal its tax, the misreading was
 * indistinguishable from the truth. Naming the operands is what makes the sum
 * unambiguous; when no part is billed on top there is no arithmetic to show,
 * so the price stands alone with the fact that the parts are inside it.
 */
function BundleArithmetic({
  economics,
  billedPartCount,
  includedPartCount,
  formatCurrency,
}: {
  economics: BundleEconomics;
  billedPartCount: number;
  includedPartCount: number;
  formatCurrency: (value: number) => string;
}) {
  // Label and figure share one element on purpose: the pair is one statement,
  // and it keeps the label from being mistaken for the card's own "Service"
  // heading by anything reading the text.
  const figure = (label: string, value: string, emphasize = false, struck = false) => (
    <span
      className={cn(
        'tabular-nums',
        emphasize && 'font-semibold text-foreground',
        struck && 'line-through',
      )}
    >
      {label} {value}
    </span>
  );
  const dot = <span className="px-1 text-muted-foreground/50">·</span>;
  // Whether there is arithmetic to show is a question of TREATMENT: a part
  // billed on its own line is an operand at any amount, $0.00 included; a
  // declined optional part is announced by the "not included" note beside
  // this and is not one.
  const hasBilledParts = billedPartCount > 0;

  if (!economics.isSold && !hasBilledParts) {
    // Nothing is being sold: the struck-through service says it all.
    return (
      <span className="ml-auto text-xs text-muted-foreground">
        {figure('Service', formatCurrency(economics.unselectedServiceAmount), false, true)}
      </span>
    );
  }

  if (economics.isSold && !hasBilledParts) {
    return (
      <span className="ml-auto text-xs text-muted-foreground">
        {includedPartCount > 0 ? (
          <>
            <span className="text-muted-foreground/70">parts included in the service price</span>
            {dot}
          </>
        ) : null}
        {figure('Subtotal', formatCurrency(economics.billedBundleTotal), true)}
      </span>
    );
  }

  return (
    <span className="ml-auto text-xs text-muted-foreground">
      {economics.isSold
        ? figure('Service', formatCurrency(economics.serviceAmount))
        : figure('Service', formatCurrency(economics.unselectedServiceAmount), false, true)}
      {dot}
      {figure(
        economics.isSold ? 'Billed parts' : 'Parts separately',
        formatCurrency(economics.billedPartsAmount),
      )}
      {dot}
      {figure('Subtotal', formatCurrency(economics.billedBundleTotal), true)}
    </span>
  );
}

/**
 * The parts in the sale, split by how they charge: on a line of their own, or
 * inside the service's price. By treatment (`componentBillsOwnLine`), never by
 * amount — a billed part at $0.00 is still billed.
 *
 * `parentSold` is the service's own state: a part INCLUDED in a service that
 * is not being sold had no line but that service's, so it leaves the sale with
 * it and is counted as neither. A billed part is its own line and stands.
 */
export function partCounts<T extends ComponentGroupableLine>(
  components: T[],
  parentSold = true,
  isRolledUp = false,
) {
  // A declined service takes ALL of its parts out of the sale — included and
  // billed extra alike (#2452, `declineParts`) — so under an unsold parent
  // there is nothing to count on either side.
  const sold = parentSold ? components.filter((c) => c.isSelected !== false) : [];
  const billedPartCount = sold.filter((c) => componentBillsOwnLine(c)).length;
  return {
    billedPartCount,
    includedPartCount: sold.length - billedPartCount,
    // How many of them DRAW A ROW on the customer's document, which is a
    // different question from how many charge: a visible included EQUIPMENT
    // prints as "Included", a non-chargeable MATERIAL prints nothing at all
    // (`rendersOnCustomerDocument`, and "the four states" in the spec).
    // Under ROLLED_UP the rest are folded into the service's own row, so only
    // what `unfoldsOntoItsOwnLine` unfolds draws one of its own.
    customerRowCount: sold.filter((c) =>
      isRolledUp
        ? unfoldsOntoItsOwnLine(c as never)
        : rendersOnCustomerDocument(c as LineEconomics),
    ).length,
  };
}

/** What a grouped row is, so a caller can indent parts under their service. */
export type GroupedRowRole = 'standalone' | 'service' | 'part';

/**
 * The same grouping, for surfaces whose rows are `<tr>`s.
 *
 * A bordered card cannot wrap table rows, so the bundle is framed by a spanning
 * summary row above the service and by the caller indenting the parts beneath
 * it. The decision of WHAT belongs together is not re-made here — it comes from
 * `analyzeBundles`, the same call `renderGroupedLines` makes — because two
 * implementations of that is how a document and its detail view start
 * disagreeing about which lines are one sale.
 */
export function renderGroupedTableRows<T extends BundleGroupableLine>(args: {
  lineItems: T[];
  /** Columns the summary row must span. */
  columnCount: number;
  renderRow: (item: T, index: number, role: GroupedRowRole) => ReactNode;
  formatCurrency: (value: number) => string;
  amountOf?: (item: T) => number;
  /** Viewer cannot see pricing: keep the framing, drop every figure. */
  hideAmounts?: boolean;
}): ReactNode[] {
  const { lineItems, columnCount, renderRow, formatCurrency, amountOf, hideAmounts } = args;
  const indexOf = new Map<T, number>();
  lineItems.forEach((item, index) => indexOf.set(item, index));

  const out: ReactNode[] = [];

  for (const { group, economics, warning } of analyzeBundles(lineItems, amountOf)) {
    const parentIndex = indexOf.get(group.parent) ?? 0;
    const key = group.parent.lineKey ?? `row-${parentIndex}`;

    if (group.components.length === 0) {
      out.push(<Fragment key={key}>{renderRow(group.parent, parentIndex, 'standalone')}</Fragment>);
      continue;
    }

    out.push(
      <TableRow
        key={`${key}-bundle`}
        className="border-t-2 border-primary/25 bg-primary/[0.04] hover:bg-primary/[0.04]"
      >
        <TableCell colSpan={columnCount} className="py-1.5 whitespace-normal">
          <BundleSummary
            economics={economics}
            isRolledUp={group.isRolledUp}
            serviceDrawsRow={rendersOnCustomerDocument(group.parent as LineEconomics)}
            partCount={group.components.length}
            {...partCounts(group.components, group.parent.isSelected !== false, group.isRolledUp)}
            formatCurrency={formatCurrency}
            hideAmounts={hideAmounts}
          />
        </TableCell>
      </TableRow>,
      <Fragment key={`${key}-service`}>{renderRow(group.parent, parentIndex, 'service')}</Fragment>,
      ...group.components.map((component, componentIndex) => (
        <Fragment key={`${key}-part-${component.lineKey ?? componentIndex}`}>
          {renderRow(component, indexOf.get(component) ?? 0, 'part')}
        </Fragment>
      )),
    );

    if (warning) {
      out.push(
        <TableRow key={`${key}-warning`} className="hover:bg-transparent">
          <TableCell colSpan={columnCount} className="p-0 whitespace-normal">
            <BundleWarningBanner warning={warning} />
          </TableCell>
        </TableRow>,
      );
    }
  }

  return out;
}

/**
 * Render a flat line list with each service and its parts framed as one bundle.
 *
 * The caller keeps rendering its own rows — this only decides which rows belong
 * together and wraps them. That is what lets estimates, jobs, invoices and
 * proposals share the framing without giving up their row internals or their
 * index-based update callbacks.
 *
 * Indexes are taken from the ORIGINAL array, because every surface's updaters
 * (`updateQuantity(index, …)`, `removeLineItem(index)`) address lines
 * positionally. Re-deriving an index from the grouped order would silently edit
 * the wrong line.
 */
export function renderGroupedLines<T extends BundleGroupableLine>(args: {
  lineItems: T[];
  renderRow: (item: T, index: number) => ReactNode;
  formatCurrency: (value: number) => string;
  /** Amount used for the bundle's economics; defaults to the line subtotal. */
  amountOf?: (item: T) => number;
  /** Viewer cannot see pricing: keep the framing, drop every figure. */
  hideAmounts?: boolean;
  onDisplayModeChange?: (index: number, mode: 'ITEMIZED' | 'ROLLED_UP') => void;
  /** Dynamic document services configure their parts inside the parent wizard. */
  componentsInlineFor?: (parent: T) => boolean;
}): ReactNode[] {
  const { lineItems, renderRow, formatCurrency, amountOf, hideAmounts } = args;
  const indexOf = new Map<T, number>();
  lineItems.forEach((item, index) => indexOf.set(item, index));

  return analyzeBundles(lineItems, amountOf).map(({ group, economics, warning }) => {
    const parentIndex = indexOf.get(group.parent) ?? 0;
    const parentRow = renderRow(group.parent, parentIndex);

    // Not a bundle — an ordinary line renders exactly as it always did.
    if (group.components.length === 0) {
      return <Fragment key={group.parent.lineKey ?? parentIndex}>{parentRow}</Fragment>;
    }

    if (args.componentsInlineFor?.(group.parent)) {
      return <Fragment key={group.parent.lineKey ?? parentIndex}>{parentRow}</Fragment>;
    }

    return (
      <ServiceBundleGroup
        key={group.parent.lineKey ?? parentIndex}
        serviceName={group.parent.name ?? 'Service'}
        economics={economics}
        warning={warning}
        isRolledUp={group.isRolledUp}
        treatment={group.parent.taxLineType as TaxLineType | null}
        serviceDrawsRow={rendersOnCustomerDocument(group.parent as LineEconomics)}
        partCount={group.components.length}
        {...partCounts(group.components, group.parent.isSelected !== false, group.isRolledUp)}
        formatCurrency={formatCurrency}
        laborHours={serviceLineHours(group.parent)}
        laborDetails={serviceLineLaborDetails(group.parent)}
        hideAmounts={hideAmounts}
        parts={group.components.map((component) =>
          renderRow(component, indexOf.get(component) ?? 0),
        )}
        onDisplayModeChange={
          args.onDisplayModeChange
            ? (mode) => args.onDisplayModeChange!(parentIndex, mode)
            : undefined
        }
      >
        {parentRow}
      </ServiceBundleGroup>
    );
  });
}

function BundleTaxControls({
  economics,
  billedPartCount,
  customerRowCount,
  serviceDrawsRow,
  isRolledUp,
  treatment,
  readOnly,
  formatCurrency,
  serviceName,
  hideAmounts,
  onDisplayModeChange,
}: {
  economics: BundleEconomics;
  /** How many parts print a line of their own — see `partCounts`. */
  billedPartCount: number;
  /** How many parts draw a ROW on the customer's document — see `partCounts`. */
  customerRowCount: number;
  /** Does the service itself draw one? A hidden service does not. */
  serviceDrawsRow: boolean;
  isRolledUp: boolean;
  treatment: TaxLineType | null | undefined;
  readOnly: boolean;
  formatCurrency: (value: number) => string;
  serviceName: string;
  hideAmounts: boolean;
  onDisplayModeChange?: (mode: 'ITEMIZED' | 'ROLLED_UP') => void;
}) {
  const customerLineCount = (economics.isSold && serviceDrawsRow ? 1 : 0) + customerRowCount;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-primary/15 px-4 py-2.5">
      <div className="flex items-center gap-1.5">
        <Receipt size={12} className="shrink-0 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Customer sees</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <Info size={12} className="shrink-0 cursor-help text-muted-foreground" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-sm">
            {/* With the service deselected there is no bundle to roll up, so the
                rolled-up copy would describe tax that is not what gets charged. */}
            {isRolledUp && economics.isSold
              ? DISPLAY_MODE_TAX_HINT.ROLLED_UP
              : DISPLAY_MODE_TAX_HINT.ITEMIZED}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* The stored mode still edits here, but it decides nothing while the
          service is out of the sale — say so rather than letting the control
          read as the treatment the customer will actually get. */}
      {!economics.isSold ? (
        <span className="text-xs text-muted-foreground">
          Not applied — the service is not selected
        </span>
      ) : null}

      {/* The MODE's name, which is what this control sets: whether the lines
          are STATED as one bundle, and so taxed as one. Deliberately not a
          claim about the document's shape (rolling up still unfolds a visible
          billed part onto its own row — the count at the end of this row says
          what prints) and not one about charging either (an included part is
          inside the service's price under both modes). */}
      {onDisplayModeChange ? (
        <Select
          value={isRolledUp ? 'ROLLED_UP' : 'ITEMIZED'}
          onValueChange={(value) => onDisplayModeChange(value as 'ITEMIZED' | 'ROLLED_UP')}
        >
          <SelectTrigger className="h-7 w-[190px] bg-background text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ROLLED_UP">Stated as one bundle</SelectItem>
            <SelectItem value="ITEMIZED">Parts stated separately</SelectItem>
          </SelectContent>
        </Select>
      ) : (
        <span className="text-xs font-medium">
          {isRolledUp ? 'Stated as one bundle' : 'Parts stated separately'}
        </span>
      )}

      {/* Only a bundle that is actually being sold is taxed on the service's
          classification. With the service out of the sale the part is taxed on
          its own, so naming the service's treatment here would answer "what is
          this taxed as?" with the wrong one. */}
      {isRolledUp && economics.isSold ? (
        <>
          <span className="text-xs text-muted-foreground">·</span>
          <span className="text-xs text-muted-foreground">Taxed as</span>
          {/* Read-only: the SERVICE's own classification governs the whole
              bundle, so it is changed on the pricebook item rather than per
              line. Showing it here is what makes "why is there no materials
              tax?" answerable without leaving the estimate. */}
          <span className="text-xs font-medium capitalize">
            {(treatment ?? 'flat rate').replace('_', ' ')}
          </span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info size={12} className="shrink-0 cursor-help text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-sm">
              The whole bundle is taxed on the service&rsquo;s own classification, so parts folded
              into a service are never taxed as materials. What that actually costs is decided by
              this address&rsquo;s tax codes — some states tax service labor, others exempt it.
              Change the classification on the pricebook item.
            </TooltipContent>
          </Tooltip>
        </>
      ) : null}

      {/* How many lines the customer's document actually gets: the service
          when it is sold, plus every part billed on a line of its own. A
          declined service contributes none, so its one surviving billed part
          is ONE line — "several" was counting a service nobody bought. */}
      {/* Only when the document actually PRINTS something. "Has billed parts"
          is not that: an unsold service whose one billed part is hidden charges
          $25 and draws no row at all, and this said "across one line" over a
          document with none. `customerLineCount` is the count of rows, so zero
          rows means there is nothing here to describe. */}
      {!readOnly && !hideAmounts && customerLineCount > 0 ? (
        <span className="ml-auto text-xs text-muted-foreground">
          {/* The customer never reads the name of a service nobody bought. */}
          {/* What the customer is CHARGED — the billed figure, the same one the
              header foots to. An included part's own figure is inside the
              service's price, not a second amount the customer reads. How many
              LINES that comes to is a count, not an amount: a part billed on
              its own line prints one at $0.00 too. */}
          {/* Naming the service as THE line the customer reads is only true
              when there is exactly one: a hidden rolled-up service prints no
              row carrying its name, and a rolled-up bundle still unfolds a
              visible billed part onto a row of its own. Otherwise the count
              speaks. */}
          {isRolledUp && economics.isSold && serviceDrawsRow && customerLineCount === 1
            ? `“${serviceName}” · ${formatCurrency(economics.billedBundleTotal)}`
            : `${formatCurrency(economics.billedBundleTotal)} across ${
                customerLineCount > 1 ? 'several lines' : 'one line'
              }`}
        </span>
      ) : null}
    </div>
  );
}

function BundleWarningBanner({ warning }: { warning: BundleTaxWarning }) {
  const isHigh = warning.level === 'high';
  return (
    <div
      // Advisory, not an error: the call depends on the jurisdiction and on
      // whether the work is a capital improvement, neither of which this screen
      // can settle. `role="status"` keeps it out of assertive announcements.
      role="status"
      className={cn(
        'flex gap-2.5 border-t px-4 py-3',
        isHigh ? 'border-destructive/30 bg-destructive/5' : 'border-amber-500/30 bg-amber-500/5',
      )}
    >
      <AlertTriangle
        size={13}
        className={cn('mt-0.5 shrink-0', isHigh ? 'text-destructive' : 'text-amber-600')}
      />
      <div className="min-w-0">
        <p className={cn('text-xs font-medium', isHigh ? 'text-destructive' : 'text-amber-700')}>
          {warning.title}
        </p>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">{warning.body}</p>
      </div>
    </div>
  );
}
